// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris


import React from "react";
import ReactDOM from "react-dom/client";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import "./styles/global.css";
import { App } from "./App";
import { navigateTo } from "./router/navigation";
import { CustomNavigationClient } from "./routes/CustomNavigationClient";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { completeInFlightRedirect } from "./features/auth/completeRedirectLogin";
import { prepareCachedSession } from "./features/auth/prepareSession";
import { createMsalConfig } from "./authConfig";
import { AppThemeProvider } from "./context/AppThemeContext";
import { AppLocaleProvider } from "./context/AppLocaleContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { TutorialProvider } from "./features/tutorial/context/TutorialContext";
import { loadSiteConfig } from "./utils/siteConfig";
import { isEmbeddedFrame } from "./utils/frameContext";

import { MAIN_BUILD_ENV } from "./mainBuildEnv";
(globalThis as { __RIDDLE_ENV__?: Record<string, string | boolean | undefined> }).__RIDDLE_ENV__ =
  MAIN_BUILD_ENV;

function renderApp(msalInstance: PublicClientApplication) {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <AppThemeProvider>
          <AppLocaleProvider>
            <ErrorBoundary>
              <OnboardingProvider>
                <SubscriptionProvider>
                  <TutorialProvider>
                    <App />
                  </TutorialProvider>
                </SubscriptionProvider>
              </OnboardingProvider>
            </ErrorBoundary>
          </AppLocaleProvider>
        </AppThemeProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

async function bootstrap(): Promise<void> {
  await loadSiteConfig();

  const msalInstance = new PublicClientApplication(createMsalConfig());
  await msalInstance.initialize();
  msalInstance.setNavigationClient(new CustomNavigationClient(navigateTo));

  if (isEmbeddedFrame()) {
    try {
      await msalInstance.handleRedirectPromise();
    } catch {
    }
    return;
  }

  try {
    await completeInFlightRedirect(msalInstance);
  } catch {
  }

  await prepareCachedSession(msalInstance);

  renderApp(msalInstance);
}

void bootstrap();
